import { useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '@/api/queryKeys';
import { FeatureName, ResponseFeatures } from '@/api/types';

import api from '@/api';

interface UseFeatureManagementHookReturn {
  isLoading: boolean;
  hasBeenFetched: boolean;
  refetch: () => Promise<void>;
  isFeatureAvailable: (key: FeatureName) => boolean;
  getDetails: <T extends FeatureName>(key: T) => ResponseFeatures[T][1];
}

export const useFeatureManagement = (isLoggedIn: boolean): UseFeatureManagementHookReturn => {
  const {
    isLoading,
    data: features,
    isFetched,
    refetch: queryRefetch,
  } = useQuery<ResponseFeatures>({
    queryKey: queryKeys.features.list(),
    queryFn: () => api.getFeatures(),
    enabled: isLoggedIn,
  });

  const refetch = useCallback(async () => {
    await queryRefetch();
  }, [queryRefetch]);

  const isFeatureAvailable: UseFeatureManagementHookReturn['isFeatureAvailable'] = useCallback(
    (key) => !!features?.[key][0],
    [features],
  );

  const getDetails: UseFeatureManagementHookReturn['getDetails'] = useCallback(
    (key: keyof ResponseFeatures) => features?.[key][1],
    [features],
  );

  return {
    isLoading,
    hasBeenFetched: isFetched,
    refetch,
    isFeatureAvailable,
    getDetails,
  };
};
